import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/contexts/AuthContext";
import { 
  Calendar, 
  Clock, 
  Users, 
  CheckCircle, 
  ArrowLeft,
  Shield,
  Home
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface BookingConfirmationData {
  bookingId: string;
  date: string;
  selectedSlots: Array<{
    id: string;
    time_start: string;
    time_end: string;
  }>;
  teamMembers: Array<{
    name: string;
    phone_number?: string;
  }>;
}

const AdminBookingConfirmation = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [bookingData, setBookingData] = useState<BookingConfirmationData | null>(null);

  useEffect(() => {
    // Get booking data from navigation state
    if (location.state?.bookingData) {
      setBookingData(location.state.bookingData);
    } else {
      // If no booking data, redirect to admin dashboard
      navigate("/admin-dashboard");
    }
  }, [location.state, navigate]);

  const formatTimeSlot = (timeStart: string, timeEnd: string) => {
    const formatTime = (time: string) => {
      const [hours, minutes] = time.split(":");
      const hour = Number.parseInt(hours);
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${displayHour}:${minutes} ${ampm}`;
    };

    return `${formatTime(timeStart)} - ${formatTime(timeEnd)}`;
  };

  const handleBackToDashboard = () => {
    navigate("/admin-dashboard");
  };

  const handleViewHistory = () => {
    navigate("/admin/booking-history");
  };

  if (!bookingData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Loading confirmation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center py-4">
            <Button
              variant="ghost"
              onClick={handleBackToDashboard}
              className="mr-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <h1 className="text-2xl font-bold text-blue-900">Admin Booking Confirmation</h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Admin Booking Confirmed!
          </h2>
          <p className="text-gray-600">
            Your admin booking has been successfully created and slots are now blocked for users.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Shield className="w-5 h-5 mr-2 text-blue-600" />
                Booking Details
              </CardTitle>
              <CardDescription>
                Admin booking information and slot details
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Booking ID:</span>
                <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                  {bookingData.bookingId}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-gray-600">Date:</span>
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-2 text-gray-500" />
                  <span className="font-medium">{bookingData.date}</span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-gray-600">Total Slots:</span>
                <Badge className="bg-blue-100 text-blue-800">
                  {bookingData.selectedSlots.length} slots
                </Badge>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-gray-600">Team Size:</span>
                <div className="flex items-center">
                  <Users className="w-4 h-4 mr-2 text-gray-500" />
                  <span className="font-medium">{bookingData.teamMembers.length} members</span>
                </div>
              </div>

              <Alert>
                <Shield className="h-4 w-4" />
                <AlertDescription>
                  <strong>Status:</strong> All selected slots are now blocked for users and reserved for admin use.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Clock className="w-5 h-5 mr-2 text-green-600" />
                Time Slots
              </CardTitle>
              <CardDescription>
                Reserved time slots for admin booking
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {bookingData.selectedSlots.map((slot, index) => (
                  <div
                    key={slot.id}
                    className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
                  >
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-sm font-medium text-green-700">
                          {index + 1}
                        </span>
                      </div>
                      <span className="font-medium">
                        {formatTimeSlot(slot.time_start, slot.time_end)}
                      </span>
                    </div>
                    <Badge className="bg-green-100 text-green-800">
                      <Shield className="w-3 h-3 mr-1" />
                      Blocked
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Users className="w-5 h-5 mr-2 text-purple-600" />
                Team Members
              </CardTitle>
              <CardDescription>
                Admin team members for this booking
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {bookingData.teamMembers.map((member, index) => (
                  <div
                    key={index}
                    className="flex items-center p-3 bg-purple-50 border border-purple-200 rounded-lg"
                  >
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                      <span className="text-sm font-medium text-purple-700">
                        {member.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{member.name}</div>
                      {member.phone_number && (
                        <div className="text-sm text-gray-600">{member.phone_number}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={handleViewHistory}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Calendar className="w-4 h-4 mr-2" />
            View Booking History
          </Button>
          <Button
            variant="outline"
            onClick={handleBackToDashboard}
          >
            <Home className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </main>
    </div>
  );
};

export default AdminBookingConfirmation;